import React, { useState, useEffect, useCallback, FC } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardPreview,
  CardFooter,
  Button,
  Spinner,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogBody,
  Badge,
  Divider,
  Text,
  Title3,
  Body1,
  Title1,
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Menu,
  MenuTrigger,
  SplitButton,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuButtonProps,
  CounterBadge,
} from '@fluentui/react-components';
import { ArrowLeft24Regular, Add24Regular, Save24Regular, Delete24Regular, PreviewLink16Filled, FilterFilled, SaveRegular, DeleteRegular, BinRecycleRegular, SendRegular, FilterDismissFilled, DocumentMentionRegular, DismissSquareRegular, PeopleEditRegular, ShareFilled, SendPersonRegular } from '@fluentui/react-icons';

import { PlacementDoc, PlacementRequest, PlacementRequestReceiver } from '../../models/placements';
import { Tendering, Tender, TenderContract, TenderAnswer, Question } from '../../models/tendering';
import { ActiveFilters, Filter } from '../../models/filters';
import { User, UserSystemData } from '../../models/users';
import { Module } from '../../models/module';
import { FiltersComponent } from './FiltersComponent';
import { ContractedComponent } from './ContractedComponent';
import { PlacementsListComponent } from './PlacementsListComponent';
import { useAppContext } from '../../AppContext';
import { deletePlacementDocument, getPlacement, getPlacementRequest, updatePlacement } from '../../services/placementService';
import { getTenderingDocument, getTendersDocumentFilteredAnswers } from '../../services/tenderingService';
import { filterTenderList, initTenderList } from '../../utils/filtering';
import SendRequest from './PlacementDialogs/SendRequest';
import CancelRequest from './PlacementDialogs/CancelRequest';
import SharePlacement from './PlacementDialogs/SharePlacement';
import MovePlacement from './PlacementDialogs/MovePlacement';
import ErrorDialog from '../dialogs/ErrorDialog';

interface PlacementComponentProps {
  
}

export const PlacementComponent: FC<PlacementComponentProps> = () => {
  const { id: placementDocID } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State management
  const [tendersLoaded, setTendersLoaded] = useState(false);
  const [requestLoaded, setRequestLoaded] = useState(false);
  const [tenderingLoaded, setTenderingLoaded] = useState(false);
  const [placementLoaded, setPlacementLoaded] = useState(false);
  const [tendering, setTendering] = useState<Tendering | null>(null);
  const [baseTenders, setBaseTenders] = useState<Tender[]>([]);
  const [baseFilters, setBaseFilters] = useState<Filter[]>([]);
  const [filters, setFilters] = useState<Filter[]>([]);
  const [activeFilters, setActiveFilters] = useState<ActiveFilters | null>(null);
  const [placement, setPlacement] = useState<PlacementDoc | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<Filter | null>(null);
  const [initialFilters, setInitialFilters] = useState<ActiveFilters | null>(null);
  const [filterChanges, setFilterChanges] = useState(false);
  const [savingFilters, setSavingFilters] = useState(false);
  const [contract, setContract] = useState<TenderContract | null>(null);
  const [hasEvaluatedParts, setHasEvaluatedParts] = useState(false);
  const [filterReset, setFilterReset] = useState(false);
  const [request, setRequest] = useState<PlacementRequest | null>(null);
  const [receivers, setReceivers] = useState<PlacementRequestReceiver[]>([]);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [isSendRequestOpen, setIsSendRequestOpen] = useState(false);
  const [isCancelRequestOpen, setIsCancelRequestOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [filteredList, setFilteredList] = useState<Tender[]>([]);
  const [dialogActiveFilters, setDialogActiveFilters] = useState<ActiveFilters | null>(null);
  const [isUserGroupAdmin, setIsUserGroupAdmin] = useState<boolean>(false);
  const [isConflictDialogOpen, setIsConflictDialogOpen] = useState(false);

  const { user, featurePermissions } = useAppContext();

  // Load initial data
  useEffect(() => {
    if (!placementDocID) return;

    const loadTenderingAndPlacement = async () => {
      try {
        // Get PlacementDoc
        const placementData = await getPlacement(placementDocID);
        
        // Set reference if new placement
        if (!placementData.reference) {
          placementData.reference = placementDocID.slice(0, 8);
        }

        setPlacement(placementData);
        setActiveFilters(placementData.activeFilters);
        setPlacementLoaded(true);
        setInitialFilters(JSON.parse(JSON.stringify(placementData.activeFilters))); // Deep clone

        // Get Tendering
        const tenderingProperties = ["content.properties", "content.tenderingParts"];
        const tenderingData = await getTenderingDocument(
          placementData.tenderingID, 
          tenderingProperties
        );
        
        setTendering(tenderingData);
        setBaseFilters(tenderingData.properties.baseFilters || []);
        setFilters(tenderingData.properties.filters || []);
        setContract(tenderingData.properties.contract);
        setTenderingLoaded(true);
        setHasEvaluatedParts(tenderingData.tenderingParts.some(tp => tp.evaluated));
        // Look up the user's own entry in the group's member list - roles live there,
        // not on the top-level assignment, and a user can hold multiple roles per group.
        setIsUserGroupAdmin(
          user?.userGroupAssignments.some((uga: any) =>
            uga.userGroup.userGroupID === placementData.userGroupID &&
            uga.userGroup.userGroupAssignments?.some((member: any) =>
              member.user?.userID === user.user.userID && member.roles?.some((r: any) => r.roleName === "Behörighetsansvarig")
            )
          )
        );
        
      } catch (error) {
        console.error('Error loading placement data:', error);
      }
    };

    loadTenderingAndPlacement();
  }, [placementDocID]);

  useEffect(() => {
    const loadTendersAndRequest = async () => {
      try {
        const tenderProperties = ["content.tenderProperties", "content.tenderingParts", "content.contact"];
        const [tenderResult, placementRequestResult] = await Promise.all([
          getTendersDocumentFilteredAnswers(Number(tendering?.tenderingID), tenderProperties, getQuestions()),
          (placement?.status === 'sent' && placementDocID) ? getPlacementRequest(placementDocID) : Promise.resolve()
        ]);

        if (tendering) {
          // Initialize the tender list with all transformations in one go
          let initializedTenders = initTenderList(tenderResult, tendering, placement?.activeFilters);
          
          // Apply all transformations to the local variable — set first non-denied as ranking target
          let firstNonDeniedFound = false;
          initializedTenders = initializedTenders.map((tender) => {
            if (!tender.denied && !firstNonDeniedFound) {
              firstNonDeniedFound = true;
              return { ...tender, rankingTarget: true };
            }
            return { ...tender, rankingTarget: false };
          });
          
          // Merge with placement data
          initializedTenders = mergeTendersAndPlacement(initializedTenders);
          
          // Set the final result once
          setBaseTenders(initializedTenders);
          
          // Update placement with the final tenders
          setPlacement(prev => prev ? { ...prev, tenders: initializedTenders } : null);
        }
        
        if (placement?.status === 'sent') {
          setRequest(placementRequestResult || null);
          setPlacement(prev => prev ? { 
            ...prev, 
            tenders: mergeRequestReceiversAndTender(placementRequestResult?.placementRequestReceivers || [])
          } : null);
        } else {
          setRequestLoaded(true);
        }
        
        if (activeFilters && activeFilters.filters.length > 0 && placement?.status !== 'sent') {
          setPlacement(prev => prev ? { 
            ...prev, 
            tenders: filterTenderList((prev.tenders ?? []), activeFilters.filters) 
          } : null);
        }

        setTendersLoaded(true);
        setRequestLoaded(true);
      } catch (error) {
        console.error('Error loading placement data:', error);
      }
    };

    if (tendering) { loadTendersAndRequest(); }
  }, [tendering]);

  useEffect(() => {
    if (tendersLoaded && searchParams.get('ny') === 'true') {
      setFilterDialogOpen(true);
      setFilteredList(placement?.tenders || []);
      setDialogActiveFilters(activeFilters);
      searchParams.delete('ny');
      setSearchParams(searchParams, { replace: true });
    }
  }, [tendersLoaded, searchParams, placement?.tenders, activeFilters]);

  // Handle filter changes
  const handleFilterChange = (filter: Filter) => {
    if (!dialogActiveFilters) return;

    const newActiveFilters = { ...dialogActiveFilters };
    newActiveFilters.filters = newActiveFilters.filters.filter(f => f.questionID !== filter.questionID);

    if (filter.value) {
      newActiveFilters.filters.push(filter);
    }

    setDialogActiveFilters(newActiveFilters);
    checkFilterChanges();

    // Apply filters to tender list
    setFilteredList(filterTenderList(baseTenders, newActiveFilters.filters));
  };

  const applyFilters = () => {
    checkFilterChanges();
    setActiveFilters(dialogActiveFilters);
    if(placement) {
      const updatedPlacement: PlacementDoc = {
        ...placement,
        tenders: filteredList,
        activeFilters: dialogActiveFilters
      };
      setPlacement(updatedPlacement);
      savePlacement(updatedPlacement);
    }
  };

  const checkFilterChanges = useCallback(() => {
    if (!activeFilters || !initialFilters) {
      setFilterChanges(false);
      return;
    }

    let hasChanges = false;
    if (activeFilters.filters.length >= initialFilters.filters.length) {
      activeFilters.filters.forEach(filter => {
        const initFilter = initialFilters.filters.find(f => f.questionID === filter.questionID);
        if ((initFilter && initFilter.value !== filter.value) || !initFilter) {
          hasChanges = true;
        }
      });
    } else {
      hasChanges = true;
    }
    
    setFilterChanges(hasChanges);
  }, [activeFilters, initialFilters]);

  const savePlacement = async (placementOverride?: PlacementDoc) => {
    const currentPlacement = placementOverride ?? placement;
    if (!currentPlacement || !placementDocID) return;

    setSavingFilters(true);
    try {
      await updatePlacement(currentPlacement, placementDocID);
      setSavingFilters(false);
      setInitialFilters(JSON.parse(JSON.stringify(activeFilters)));
      checkFilterChanges();
      // Show success message
    } catch (error: any) {
      setSavingFilters(false);
      if (error?.response?.status === 409) {
        setIsConflictDialogOpen(true);
      } else {
        console.error('Error saving placement:', error);
      }
    }
  };

  const handleShare = async (finalUsers: User[]) => {
    console.log('Updating placement users to:', finalUsers);
    if (!placement || !placementDocID) return;
    
    const updatedPlacement = {
      ...placement,
      user: finalUsers
    };

    setPlacement(updatedPlacement);

    try{
      await updatePlacement(updatedPlacement, placementDocID);
    }
    catch(error){
      console.error('Error updating placement users:', error);
    }
  };

  const handleMove = async (userGroupID: number, userGroupName: string, users: User[]) => {
    if (!placement || !placementDocID) return;

    const updatedPlacement = {
      ...placement,
      user: users,
      userGroupID: userGroupID,
      userGroupName: userGroupName
    };

    try{
      await updatePlacement(updatedPlacement, placementDocID);
      // The current user loses access once moved, so navigate away instead of continuing to work on stale state
      navigate('/forfragningar');
    }
    catch(error){
      console.error('Error updating placement users:', error);
    }
  }

  // const deleteFilters = () => {
  //   if (!activeFilters) return;

  //   const clearedFilters = { ...activeFilters };
  //   clearedFilters.filters.forEach(filter => {
  //     filter.value = undefined;
  //     handleFilterChange(filter);
  //   });
  //   setFilterReset(true);
  // };

  const deleteFilter = (filter: Filter) => {
    if (!activeFilters) return;

    const newActiveFilters = { ...activeFilters };
    newActiveFilters.filters = newActiveFilters.filters.filter(f => f.questionID !== filter.questionID);
    
    setActiveFilters(newActiveFilters);
    checkFilterChanges();

    // Apply filters to tender list
    if (placement && baseTenders) {
      const filteredTenders = filterTenderList(baseTenders, newActiveFilters.filters);
      const updatedPlacement = {
        ...placement,
        tenders: filteredTenders,
        activeFilters: newActiveFilters
      };
      setPlacement(updatedPlacement);
      savePlacement(updatedPlacement);
    }
  }

  const onNewSearch = () => {
    if (tendering) {
      navigate(`/contracting/${tendering.tenderingID}`);
    }
  };

  // Check if user is author
  const isUserAuthorOfPlacement = () => {
    if (!placement?.user) return false;
  
    if (Array.isArray(placement.user)) {
      return placement.user.some((u: User) => u.userID === user.user.userID) || user.roles.some((r: any) => r.roleType === 'General');
    }
    
    return placement.user.userID === user.user.userID || user.roles.some((r: any) => r.roleType === 'General');
  };

  const canUserCheckPlacementData = (): boolean => {
    if (!placement?.user) return false;
  
    if (Array.isArray(placement.user)) {
      return placement.user.some((u: User) => u.userID === user.user.userID) || user.roles.some((r: any) => r.roleName === 'SystemAdmin');
    }
    
    return placement.user.userID === user.user.userID || user.roles.some((r: any) => r.roleName === 'SystemAdmin');
  };

  // Share/Move should only be available while the logged user is a member of the placement's current user group
  const isUserMemberOfPlacementUserGroup = (): boolean => {
    return !!user?.userGroupAssignments?.some((uga: any) => uga.userGroup.userGroupID === placement?.userGroupID);
  };

  // A plain Kommunanvändare can only Share/Move a placement they're actually assigned to (in placement.user);
  // Behörighetsansvarig aren't restricted to this since they manage the whole group's placements
  const canUserShareOrMovePlacement = (): boolean => {
    if (isUserGroupAdmin) return true;
    if (!placement?.user) return false;

    if (Array.isArray(placement.user)) {
      return placement.user.some((u: User) => u.userID === user.user.userID);
    }

    return placement.user.userID === user.user.userID;
  };

  const getQuestions = (): number[] => {
    const questions: number[] = [];

    // Add contact
    if (tendering?.properties.contact.email) { questions.push(tendering.properties.contact.email); } 
    if (tendering?.properties.contact.phone) { questions.push(tendering.properties.contact.phone); }
    
    // Check if multiple prices
    if (placement?.tenderingPart.ranking.prices) {
      placement.tenderingPart.ranking.prices.forEach((p: Question) => {
        questions.push(Number(p));
      });
    }
    else {
      questions.push(Number(placement?.tenderingPart.ranking));
    }

    if (tendering?.properties.filters && placement?.status !== 'sent') {
      tendering.properties.filters.forEach((f: Filter) => {
        // Check if is group filter
        if (f.filters) {
          f.filters.forEach((gf: Filter)=> {
            if (gf.questionID) { questions.push(Number(gf.questionID)); }
          });
        }
        else if (f.maxQuestionID || f.minQuestionID) {
          if (f.maxQuestionID) { questions.push(Number(f.maxQuestionID)); }
          if (f.minQuestionID) { questions.push(Number(f.minQuestionID)); }
        }
        else {
          if (f.questionID) { questions.push(Number(f.questionID)); }
        }
      });
    }

    if (tendering?.properties.baseFilters) {
      tendering.properties.baseFilters.forEach((f: Filter) => {
        // Check if is group filter
        if (f.filters) {
          f.filters.forEach((gf: Filter) => {
            if (gf.questionID) { questions.push(Number(gf.questionID)); }
          });
        }
        else if (f.maxQuestionID || f.minQuestionID) {
          if (f.maxQuestionID) { questions.push(Number(f.maxQuestionID)); }
          if (f.minQuestionID) { questions.push(Number(f.minQuestionID)); }
        }
        //Check if the filter has separate questions and get the value of the questionID from the activeFilters
        else if(f.separateQuestions){
          const filter = placement?.activeFilters.baseFilters.find((af: Filter) => af.filterID === f.filterID);
          //Check if a selection has been made
          if(filter){
            questions.push(filter.questionID);
          }
        }
        else {
          if (f.questionID) { questions.push(Number(f.questionID)); }
        }
      });
    }

    return questions;
  }

  const mergeRequestReceiversAndTender = (rr: PlacementRequestReceiver[]): Tender[] => {
    const merged: Tender[] = [];

    for (const tender of placement?.tenders ?? []) {
      merged.push({
        ...tender,
        ...(rr.find((r: PlacementRequestReceiver) => Number(r.placementRequestReceiverReference) === tender.tenderID))
      });
    }

    return merged;
  }

  const mergeTendersAndPlacement = (tenders: Tender[]): Tender[] => {
    const merged: Tender[] = [];

    for (const tender of tenders) {
      const found = placement?.tenders.find((t) => t.tenderID === tender.tenderID);
      if (found) {
        merged.push({
          ...tender,
          denied: found.denied,
          denyComment: found.denyComment,
          rankingTarget: found.rankingTarget
        });
      }
      else {
        merged.push({
          ...tender
        });
      }
    }

    return merged;
  }

  const primaryActionButtonProps: MenuButtonProps = {
    icon: <FilterFilled />,
    onClick: () => {
      if(placement?.status !== 'sent'){
        setFilterDialogOpen(true); 
        setFilteredList(placement?.tenders || []);
        setDialogActiveFilters(activeFilters);
      }
    },
  };

  const deletePlacement = async () => {
    await deletePlacementDocument(placementDocID!).then(() => {
      navigate('/forfragningar');
    })
  }

  const handlePlacementUpdate = (updatedPlacement: PlacementDoc) => {
    setPlacement(updatedPlacement);
    
    // Sync denied/rankingTarget status to baseTenders so filters work correctly
    setBaseTenders(prevBaseTenders => 
      prevBaseTenders.map(baseTender => {
        const updatedTender = updatedPlacement.tenders.find(t => t.tenderID === baseTender.tenderID);
        if (updatedTender) {
          return {
            ...baseTender,
            denied: updatedTender.denied,
            denyComment: updatedTender.denyComment,
            rankingTarget: updatedTender.rankingTarget
          };
        }
        return baseTender;
      })
    );
  };

  const getFilterValue = (filter: Filter): string => {
    console.log(filter);

    return filter.type == "bool" ? (filter.value == 1 ? "Ja" : "Nej") : filter.value;
  }

  if (!placementLoaded) {
    return (
      <div className="flex justify-center bg-[#ebebeb] min-h-screen">
        <div className="flex flex-col w-full items-center h-36 p-4 bg-gradient-to-r from-orange-700 to-yellow-500 rounded-b mx-auto">
          <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-md w-full max-w-[1480px] mt-4">
            <div className="flex justify-center items-center h-64">
              <Spinner size="large" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!placement) {
    return (
      <div className="flex justify-center bg-[#ebebeb] min-h-screen">
        <div className="flex flex-col w-full items-center h-36 p-4 bg-gradient-to-r from-orange-700 to-yellow-500 rounded-b mx-auto">
          <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-md w-full max-w-[1480px] mt-4">
            <div className="text-red-500">Error loading placement</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center bg-[#ebebeb] min-h-screen">
      <div className={`flex flex-col w-full items-center h-36 p-4 bg-gradient-to-r from-orange-700 to-yellow-500 rounded-b mx-auto ${filterDialogOpen ? 'blur-[0.5rem]' : ''}`}>
        <div className="bg-white border border-gray-300 rounded-lg w-full max-w-[1480px] mt-4">
          <div className="space-y-6">
            <Card className="shadow-lg">
              <Button
                appearance="subtle"
                icon={<ArrowLeft24Regular />}
                onClick={() => navigate('/forfragningar')}
                className="self-start"
              >
                Tillbaka till förfrågningar
              </Button>
              <Title1 className='p-2'>{placement.name}</Title1>
              {placement.status !== ("accepted") && placement.status !== ("cancelled") &&  <div className="p-2">
                <div className="p-4 rounded-lg border border-gray-200 bg-gray-50 flex flex-col mb-4">
                  {placement.status == "search" ? (<><Text className="block mb-2">
                    Om du vill göra en mer anpassad sökning utifrån den enskildes specifika behov, gå till anpassad sökning i vänstermarginalen. När du har gjort de sökningar du önskar visas en rangordningslista. Du kan nu klicka på och läsa om verksamheterna. Om det finns skäl att välja bort en verksamhet i rangordningslistan bockar du ur den i rutan. Om du väljer bort en verksamhet kommer du bli ombedd att ange skäl till varför. Efter att listan är förberedd för utskick ska du klicka på "Skapa förfrågan". Då kommer du att få upp frågor att besvara gällande den enskilde som gör det möjligt för verksamheten att ta ställning till en första matchning. Du ska också fylla i sista svarsdatum. När du sedan klickar på "Skicka förfrågan" kommer det ifyllda formuläret att skickas via mail till föreståndarna i de verksamheter som framgår av rangordningslistan.
                  </Text><Text className="block mb-2">
                      Din sökning sparas under fliken "Förfrågningar".
                    </Text><Text className="block mb-2">
                      När svarstiden har gått ut, gå in under fliken "Förfrågningar" och kontakta och välj verksamhet utifrån den enskildes behov och utifrån rangordningen uppifrån och ner.
                    </Text><Text className="block">
                      Efter att du har tagit kontakt med verksamheten och kommit överens om placering, klickar du på "Kontakta".
                    </Text></>) : (<Text>Nu är din förfrågan skickad!</Text>)}
                </div>
                <div className="relative inline-block">
                  <div className="flex gap-2 items-center">
                    {isUserAuthorOfPlacement() && <Menu>
                      <MenuTrigger disableButtonEnhancement>
                        {(triggerProps: MenuButtonProps) => (
                          <SplitButton
                            menuButton={triggerProps}
                            primaryActionButton={primaryActionButtonProps}
                            disabled={!tendersLoaded || !requestLoaded}
                          >
                            Filter
                          </SplitButton>
                        )}
                      </MenuTrigger>
                      
                      <MenuPopover>
                        <MenuList>
                          { activeFilters?.baseFilters.map((filter, index) => (
                            <MenuItem key={index}>
                              {filter.text}: {getFilterValue(filter)}
                            </MenuItem>
                          ))}
                          { activeFilters?.filters.map((filter, index) => (
                            <MenuItem onClick={() => {if(placement.status !== 'sent'){deleteFilter(filter)}}} key={index}>
                              <FilterDismissFilled />{filter.text}: {getFilterValue(filter)}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </MenuPopover>
                    </Menu>}
                    <>
                      {isUserMemberOfPlacementUserGroup() && canUserShareOrMovePlacement() && featurePermissions?.some(fp => fp.action == 'Share placement') && <Button icon={<ShareFilled />} onClick={() => setIsShareDialogOpen(true)}>Dela</Button>}
                      {isUserMemberOfPlacementUserGroup() && canUserShareOrMovePlacement() && featurePermissions?.some(fp => fp.action == 'Move placement') && <Button icon={<SendPersonRegular />} onClick={() => setIsMoveDialogOpen(true)}>Flytta</Button>}
                    </>
                    {isUserAuthorOfPlacement() && placement.status !== 'sent' && (
                      <>
                        {featurePermissions?.some(fp => fp.action == 'Edit ongoing placements (Not created by logged user)') && <Button disabled={(!tendersLoaded && !requestLoaded) || (placement.status === 'sent')} onClick={() => savePlacement()} className='ml-2' icon={<SaveRegular/>}>
                          Spara utkast
                        </Button>}
                        {featurePermissions?.some(fp => fp.action == 'Delete placement') && <Button disabled={(!tendersLoaded && !requestLoaded) || (placement.status === 'sent')} onClick={deletePlacement} className='ml-2' icon={<BinRecycleRegular />}>
                          Radera utkast
                        </Button>}
                      </>
                    )}
                    {(isUserAuthorOfPlacement() && placement.status === 'sent') && 
                      <Button
                        icon={<DismissSquareRegular />}
                        onClick={() => setIsCancelRequestOpen(true)}
                      >
                        Avbryt förfrågan
                      </Button>
                    }
                    {isUserAuthorOfPlacement() &&featurePermissions?.some(fp => fp.action == 'Tendering part/Send placement request') && <Button
                      appearance='primary' 
                      className='ml-2' 
                      icon={<SendRegular />}
                      disabled={(!tendersLoaded && !requestLoaded) || placement.status === 'sent' || placement.tenders.length === 0}
                      onClick={() => setIsSendRequestOpen(true)}
                    >
                      Skicka förfrågan
                    </Button>}
                    {canUserCheckPlacementData() && placement.status === 'sent' && <Button
                      className='ml-2'
                      icon={<DocumentMentionRegular />}
                      onClick={() => setIsSendRequestOpen(true)}
                    >
                      Se skickad förfrågan
                    </Button>}
                    {isUserAuthorOfPlacement() && placement.status === 'sent' && 
                      <Text>
                        Svar senast: {new Date(placement.answerDate).toLocaleDateString('sv-SE')}
                      </Text>
                    }
                  </div>
                  {isUserAuthorOfPlacement() && <CounterBadge 
                    className="absolute -translate-y-10 translate-x-20 z-10" 
                    count={((activeFilters?.filters?.length || 0) + (activeFilters?.baseFilters?.length || 0))} 
                    showZero={false}
                  />}
                </div>
              </div>}
              {!isUserAuthorOfPlacement() && <div className="p-4 bg-green-100 border border-green-400 text-green-700">
                <Text weight="semibold">Behörigheter för denna förfrågan saknas</Text>
              </div>}
              {isUserAuthorOfPlacement() && placement.status !== 'accepted' && (
                <div>
                  {placement.status === 'cancelled' && (
                    <div className="p-4 bg-red-100 border border-red-400 text-red-700">
                      <Text weight="semibold">Förfrågan har avbrutits. Anledning: {placement.cancellationReason}</Text>
                    </div>
                  )}
                  {tenderingLoaded && (
                    <div>
                      {tendersLoaded && requestLoaded ? (
                        <PlacementsListComponent
                          tenderList={placement.tenders}
                          placement={placement}
                          placementDocID={placementDocID || ''}
                          onPlacementUpdate={handlePlacementUpdate}
                          disableTenderClick={user.modules?.some((m: Module) => m.moduleID === placement.tenderingID && m.isHidden)}
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center space-y-4 h-64">
                          <Text size={500}>
                            {placement.status !== 'sent' 
                              ? "Söker efter matchande verksamheter..."
                              : "Laddar..."
                            }
                          </Text>
                          <Spinner size="large" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {isUserAuthorOfPlacement() && placement.status === 'accepted' && placementDocID && contract && user && tendering && (
                <ContractedComponent
                  contract={contract}
                  placement={placement}
                  placementDocID={placementDocID}
                  baseTenders={baseTenders}
                  tenderingDocument={tendering}
                />
              )}
            </Card>
          </div>
        </div>
      </div>

      <Dialog
        open={filterDialogOpen}
        onOpenChange={(_event, data) => !data.open}
      >
        <DialogSurface style={{ 
           maxHeight: '86vh',
           overflow: 'auto'
          }}>
          <DialogTitle>Filter</DialogTitle>
          <Divider className='py-2' />
          <div className='max-h-[70vh] overflow-auto'>
            <div>
            {filters.map((filter, index) => (
              <div className="p-1" key={index}>
                {filter.type === 'group' ? (
                  <Accordion collapsible>
                    <AccordionItem value={filter.filterID}>
                      <AccordionHeader>{filter.text}</AccordionHeader>
                      <AccordionPanel>
                        {filter.filters.map((subFilter: Filter) => (
                          <FiltersComponent
                            key={subFilter.filterID}
                            filter={subFilter}
                            activeFilters={activeFilters}
                            disabled={savingFilters}
                            onFilterChange={handleFilterChange}
                            />
                          ))}
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                ) : (
                  !filterReset && (
                    <FiltersComponent
                    filter={filter}
                    activeFilters={activeFilters}
                    disabled={savingFilters}
                    onFilterChange={handleFilterChange}
                    />
                  )
                )}
              </div>
            ))}
            </div>
          </div>
          <Divider className='py-2' />
          <DialogActions>
            <Button appearance="secondary" onClick={() => {
                  setFilterDialogOpen(false)
                }
              }>Avbryt</Button>
            <Button appearance="primary" onClick={() => {
              setFilterDialogOpen(false);
              applyFilters();
            }}>Visa {filteredList.length} (av {baseTenders.length})</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>

      {placement && placementDocID && (
        <SendRequest
          isOpen={isSendRequestOpen}
          onClose={() => setIsSendRequestOpen(false)}
          tenderList={placement.tenders}
          placement={placement}
          placementDocID={placementDocID}
          requestSent={handlePlacementUpdate}
          tenderingName={tendering?.tenderingName}
        />
      )}
      {placement && placementDocID && (
        <CancelRequest
          isOpen={isCancelRequestOpen}
          onClose={() => setIsCancelRequestOpen(false)}
          placement={placement}
          placementDocID={placementDocID}
          cancelSent={handlePlacementUpdate}
        />
      )}
      {placement && (
        <SharePlacement
          open={isShareDialogOpen}
          onClose={() => setIsShareDialogOpen(false)}
          onShare={handleShare}
          existingUsers={Array.isArray(placement.user) ? placement.user : [placement.user]}
          userGroupID={placement.userGroupID}
          moduleID={placement.tenderingID}
          organizationID={placement.organizationID}
        />
      )}
      {placement && (
        <MovePlacement
          open={isMoveDialogOpen}
          onClose={() => setIsMoveDialogOpen(false)}
          onMove={handleMove}
          moduleID={placement.tenderingID}
          organizationID={placement.organizationID}
          currentUserGroupID={placement.userGroupID}
          currentUserGroupName={placement.userGroupName}
          isUserGroupAdmin={isUserGroupAdmin}
        />
      )}

      <ErrorDialog
        isOpen={isConflictDialogOpen}
        onClose={() => {setIsConflictDialogOpen(false); window.location.reload()}}
        message="Placeringen har redan uppdaterats i en annan flik. Sidan kommer att laddas om."
      />
    </div>
  );
};

export default PlacementComponent;
